import React, { useRef, useState, useEffect } from "react";
import { motion, useMotionValue, AnimatePresence } from "framer-motion";
import { AlertTriangle, FileWarning, CheckCircle2, TrendingUp, Sparkles, MoveHorizontal, Gauge, Zap } from "lucide-react";
import { SectionHeading } from "./Section";
import { useLang } from "../../i18n";

export const Transformation = () => {
  const { t } = useLang();
  const tr = t.transformation;
  const trackRef = useRef(null);
  const x = useMotionValue(0);
  const [progress, setProgress] = useState(0);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const measure = () => setWidth(trackRef.current?.offsetWidth || 0);
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  useEffect(() => {
    const unsub = x.on("change", (v) => {
      if (width) setProgress(Math.min(1, Math.max(0, v / width)));
    });
    return unsub;
  }, [x, width]);

  const isAI = progress > 0.5;
  const metrics = isAI ? tr.after : tr.before;
  const p = progress;
  const lerp = (a, b) => a + (b - a) * p;
  const clamp01 = (v) => Math.min(1, Math.max(0, v));
  // red (239,68,68) -> flame (255,106,0)
  const mix = (a, b) => Math.round(lerp(a, b));
  const accent = `rgb(${mix(239, 255)},${mix(68, 106)},${mix(68, 0)})`;
  const dashOpacity = clamp01((p - 0.2) / 0.7);

  const items = [
    { cx: 12, cy: 16, r: -6, oy: 19, txt: "Order #4471 — missing data", done: "Order #4471 — validated" },
    { cx: 47, cy: 24, r: 5, oy: 34, txt: "Stock mismatch — warehouse B", done: "Stock synced — warehouse B" },
    { cx: 22, cy: 54, r: -3, oy: 49, txt: "Email thread: 38 replies", done: "Auto-summarised & assigned" },
    { cx: 58, cy: 60, r: 7, oy: 64, txt: "Schedule conflict — OR3", done: "OR3 re-scheduled by AI" },
    { cx: 70, cy: 34, r: -8, oy: 79, txt: "Spreadsheet v17_final_FINAL", done: "Single source of truth" },
  ];

  const speed = (1 + 11 * p).toFixed(p < 0.05 ? 0 : 1);
  const order = Math.round(p * 100);

  return (
    <section className="relative py-24 md:py-36 bg-ink-surface/40" data-testid="transformation-section">
      <div className="mx-auto max-w-[1200px] px-6 md:px-12">
        <SectionHeading overline={tr.overline} title={tr.title} sub={tr.sub} align="center" />

        <div className="mt-14 rounded-3xl border border-white/10 bg-ink p-5 md:p-8">
          {/* viewport */}
          <div className="relative h-[360px] md:h-[400px] overflow-hidden rounded-2xl border border-white/10 bg-[#0a0a0c]">
            {/* status bar */}
            <div className="absolute top-0 inset-x-0 z-20 flex items-center px-5 py-3 text-xs font-mono border-b border-white/5 bg-[#0a0a0c]/80">
              <span className="flex items-center gap-2" style={{ opacity: 1 - p }}>
                <AlertTriangle size={14} className="text-red-400" />
                <span className="text-red-400/90">SYSTEM: REACTIVE / MANUAL</span>
              </span>
              <span className="absolute left-5 flex items-center gap-2" style={{ opacity: p }}>
                <Sparkles size={14} className="text-flame" />
                <span className="text-flame">SYSTEM: AI-POWERED / PROACTIVE</span>
              </span>
            </div>

            {/* morphing cards: scattered chaos -> aligned, resolved list */}
            {items.map((n, i) => {
              const left = lerp(n.cx, 4);
              const top = lerp(n.cy, n.oy);
              const rot = n.r * (1 - p);
              const resolved = p > 0.5;
              return (
                <motion.div
                  key={i}
                  className="absolute z-10 flex items-center gap-2 rounded-lg px-3 py-2 w-[210px] backdrop-blur-sm"
                  style={{
                    left: `${left}%`,
                    top: `${top}%`,
                    rotate: `${rot}deg`,
                    borderWidth: 1,
                    borderStyle: "solid",
                    borderColor: accent.replace("rgb", "rgba").replace(")", ",0.35)"),
                    backgroundColor: accent.replace("rgb", "rgba").replace(")", ",0.07)"),
                  }}
                  animate={p < 0.12 ? { y: [0, -4, 0] } : { y: 0 }}
                  transition={p < 0.12 ? { repeat: Infinity, duration: 2 + i * 0.3 } : { duration: 0.3 }}
                >
                  {resolved ? <CheckCircle2 size={13} style={{ color: accent }} className="shrink-0" />
                    : (i % 2 ? <AlertTriangle size={13} style={{ color: accent }} className="shrink-0" />
                      : <FileWarning size={13} style={{ color: accent }} className="shrink-0" />)}
                  <span className="truncate text-[11px]" style={{ color: resolved ? "rgba(255,255,255,0.8)" : "rgba(255,180,180,0.85)" }}>
                    {resolved ? n.done : n.txt}
                  </span>
                </motion.div>
              );
            })}

            {/* AI dashboard emerging on the right */}
            <div
              className="absolute right-4 top-16 bottom-4 w-[42%] hidden sm:flex flex-col gap-3"
              style={{ opacity: dashOpacity, transform: `translateX(${(1 - dashOpacity) * 24}px)` }}
            >
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-xl border border-flame/20 bg-flame/[0.06] p-3">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-white/40"><Zap size={11} className="text-flame" /> Operating speed</div>
                  <div className="mt-1 font-heading text-2xl font-bold text-flame tabular-nums">{speed}x</div>
                </div>
                <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-white/40"><Gauge size={11} className="text-flame" /> Order</div>
                  <div className="mt-1 font-heading text-2xl font-bold text-white tabular-nums">{order}%</div>
                </div>
              </div>
              <div className="flex-1 rounded-xl border border-white/10 bg-white/[0.03] p-3 flex flex-col">
                <div className="flex items-center justify-between text-[10px] text-white/40 mb-2">
                  <span>Throughput</span>
                  <span className="flex items-center gap-1 text-flame"><TrendingUp size={11} /> live</span>
                </div>
                <div className="flex items-end gap-1.5 flex-1">
                  {[0.45, 0.6, 0.5, 0.78, 0.66, 0.9, 0.82, 1].map((v, i) => (
                    <div key={i} className="flex-1 rounded-sm bg-gradient-to-t from-flame/40 to-flame self-stretch flex items-end">
                      <div className="w-full rounded-sm bg-gradient-to-t from-flame/40 to-flame transition-[height] duration-200" style={{ height: `${v * 100 * clamp01((p - 0.15) / 0.85)}%` }} />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* progress glow sweeping with the drag */}
            <div className="absolute top-12 bottom-0 w-24 pointer-events-none -translate-x-1/2" style={{ left: `${p * 100}%`, background: "radial-gradient(ellipse at center, rgba(255,106,0,0.18), transparent 70%)" }} />
          </div>

          {/* slider track */}
          <div className="mt-6 flex items-center gap-4">
            <span className={`text-sm font-heading font-bold transition-colors ${!isAI ? "text-red-400" : "text-white/30"}`}>{tr.chaos}</span>
            <div ref={trackRef} className="relative flex-1 h-2 rounded-full bg-white/10">
              <motion.div className="absolute left-0 top-0 h-full rounded-full bg-flame" style={{ width: `${progress * 100}%` }} />
              <motion.button
                drag="x"
                dragConstraints={trackRef}
                dragElastic={0}
                dragMomentum={false}
                style={{ x }}
                data-cursor="hover"
                data-testid="transformation-handle"
                className="absolute -top-3 -ml-4 flex h-8 w-8 cursor-grab active:cursor-grabbing items-center justify-center rounded-full bg-flame text-black shadow-[0_0_20px_rgba(255,106,0,0.6)]"
                whileTap={{ scale: 1.15 }}
              >
                <MoveHorizontal size={16} />
              </motion.button>
            </div>
            <span className={`text-sm font-heading font-bold transition-colors ${isAI ? "text-flame" : "text-white/30"}`}>{tr.ai}</span>
          </div>

          {/* metrics */}
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-3">
            {metrics.map((m, i) => (
              <div key={i} className="rounded-xl border border-white/10 bg-white/[0.02] p-4">
                <div className="text-[11px] uppercase tracking-wide text-white/40">{m.l}</div>
                <AnimatePresence mode="wait">
                  <motion.div
                    key={m.v}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration: 0.3 }}
                    className={`mt-1 font-heading text-xl font-bold ${isAI ? "text-flame" : "text-white"}`}
                  >
                    {m.v}
                  </motion.div>
                </AnimatePresence>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
